/**
 * Collection of utility functions for quotes
 */

import { QuoteDataWithExtras, QuoteStatusJAWS } from '../queries/quotes'
import { ERROR_TYPES } from './types'

const ANONYMOUS_CUSTOMER = 'Anonymous Customer'
export enum QuoteErrorTypes {
  ARCHIVED = 'archived',
  NO_CUSTOMER = 'no-customer',
  NO_PRODUCTS = 'no-products',
}

/**
 * @note This type is the same as logAndReject in the sale-loader.ts file.
 * It is a workaround to avoid dependencies headaches when testing the file.
 * This is also the reason this function accepts a LogFunction as a parameter instead of using the log function from the sale-loader.ts file
 */
export type LogFunction = (
  type: string,
  message: string,
  ...logParams: any[]
) => Promise<never>

/**
 *  Check if the quote is archived or has no customer and log an error if so
 * @note This function accepts "any" log function (as long as is of type LogFunction) as a workaround to avoid dependencies headaches when testing the file.
 * However, the log function should be the logAndReject function from the sale-loader.ts file.
 * @param quote QuoteDataWithExtras  The quote to check
 * @param log LogFunction The log function to use
 * @returns Promise<never> | null A promise that rejects with the error if the quote is archived or has no customer, or null if the quote is valid
 */
export const validateQuoteAndHandleError = (
  quote: QuoteDataWithExtras,
  log: LogFunction
): PromiseLike<never> | null => {
  const isQuoteArchived = quote.status === QuoteStatusJAWS.ARCHIVED
  const { id, firstName, lastName, companyName, code } = quote.customer

  const isQuoteWithoutCustomer =
    !id ||
    !(firstName || lastName || companyName) ||
    code === ANONYMOUS_CUSTOMER

  const isQuoteWithValidProducts =
    quote.quoteProducts.length > 0 &&
    quote.quoteProducts.every(({ product }) => {
      return product?.name
    })

  if (isQuoteArchived) {
    return log(
      ERROR_TYPES.LOAD_QUOTE_ABORTED_ARCHIVED,
      QuoteErrorTypes.ARCHIVED,
      '' // the values will be logged in the catch block
    ) as Promise<never> // this is a workaround for the type error in sale-loader.ts
  } else if (isQuoteWithoutCustomer) {
    return log(
      ERROR_TYPES.LOAD_QUOTE_ABORTED_NO_CUSTOMER,
      QuoteErrorTypes.NO_CUSTOMER,
      '' // the values will be logged in the catch block
    ) as Promise<never> // this is a workaround for the type error in sale-loader.ts
  } else if (!isQuoteWithValidProducts) {
    return log(
      ERROR_TYPES.LOAD_QUOTE_ABORTED_NO_PRODUCTS,
      QuoteErrorTypes.NO_PRODUCTS,
      '' // the values will be logged in the catch block
    ) as Promise<never> // this is a workaround for the type error in sale-loader.ts
  }

  return null
}
